import Phaser from 'phaser';
import { colors } from '../config';
import RoundedRect from './RoundedRect';
import TimeSignature from '../../Models/TimeSignature';
import { TimingOffsets, TimingOffsetsConfig } from 'Models/EventStream';
import { ProgressState } from './Progress';
import IPlayable from './Playable';
import { isCapacitorNative } from 'Utils/Constants';
import MuseFlowAudio from 'Utils/MuseFlowAudioPlugin';
import { metronomeSoundConfig } from 'Volume/volumeOutput';

export type StartTiming = 'early' | 'perfect' | 'late';

export type StaffNote = {
  position: { x: number; y: number };
  durationInMs: number;
  // duration relative to a whole note, i.e, 1/4 for quarter notes,
  // 1/2 for half notes and so on
  durationRelative: number;
  playable?: IPlayable;
  isRest?: boolean;
  clef?: 'treble' | 'bass';
};

export type NoteEvent = {
  note: StaffNote;
  timestamp: number;
  offsetTime: TimingOffsets;
  onsetTime: TimingOffsets;
};

export type NoteStream = StaffNote[];

// How far before a note's onset a key may land (as a share of a whole note,
// scaled by the timing config's onset percentages).
const ONSET_START_ALLOWANCE = 0.4;

/// Describes the cursor game object and all related logic for moving it around the track.
export default class Cursor extends RoundedRect {
  /// Handle to the tween that is spawned for moving the cursor across the screen.
  clickHandle: Phaser.Tweens.Tween | null = null;
  /// Metronome handle
  //clickHandle: Phaser.Time.TimerEvent | null = null;
  click?: Phaser.Sound.BaseSound;
  exercise: Phaser.Scene;
  tempo: number;
  started: boolean = false;
  isMoving = false;

  nextTick: number = 0;
  lastTick: number = 0;

  msPerTick: number;
  timeSignature: TimeSignature;
  lastTimeUpdate: number = 0;
  lastNoteTime: number = 0;

  paused = false;
  wasPausedLastFrame = false;
  resumedThisFrame = false;
  cursorData: NoteStream;
  currentNoteIndex: number = -1;

  onDone?: () => void;
  onDoneCbScope: object | undefined;

  audioPaused: boolean = false;

  runTime: number = 0;

  offsetsConfig: TimingOffsetsConfig;
  notesRemaining: NoteEvent[] = [];
  notesPlaying: NoteEvent[] = [];
  notesPassed: NoteEvent[] = [];

  firstStart = false;

  totalBeats: number = 0;
  beatsPlayed: number = 0;

  /** False in a pass nobody plays (ungraded): passing a note does not mark it missed. */
  marksMissedNotes = true;
  // Notes passed unplayed while marksMissedNotes was false.
  private unmarkedMisses: NoteEvent[] = [];
  // Tap input: a key pressed just before the run starts on its own, how many
  // ms before, and whether it has been released since.
  private pressBeforeStart: { earlyMs: number; released: boolean } | null = null;

  // When the metronome's last beat started (frame time), kept when the cursor
  // stops, and the latest frame time, kept while it is stopped.
  private lastBeatTime = 0;
  private lastFrameTime = 0;

  // Ungraded: start on its own after a count-in of `beats` clicks (see startAfterCountIn).
  private autoStart: {
    beats: number;
    clicksHeard: number;
    onStart: () => void;
    onDone: () => void;
    scope?: object;
  } | null = null;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
    timeSig: TimeSignature,
    bpm: number,
    cursorData: NoteStream,
  ) {
    super(scene, x, y, width, height, radius, colors.cursor, 0);
    this.firstStart = true;
    this.offsetsConfig = new TimingOffsetsConfig();
    this.exercise = scene;
    this.alpha = 0;
    this.tempo = bpm;
    //this.draw();
    if (!isCapacitorNative() && this.scene.cache.audio.exists('click')) {
      this.click = this.scene.sound.add('click');
    }
    this.msPerTick = (60 / this.tempo) * 1000;
    scene.sound.pauseOnBlur = false;
    this.cursorData = cursorData;
    this.timeSignature = timeSig;
    this.addNoteEvents(cursorData);
  }

  addNoteEvents(cursorData: NoteStream) {
    // the sequence of notes passed to cursor data are assumed
    // to arranged in increasing order of their timestamps
    let t = 0;
    const events = [];
    for (const note of cursorData) {
      const noteDuration = note.durationRelative;
      const onsetTimestamp = t;
      const offsetTimestamp = onsetTimestamp + note.durationRelative;
      const correctNoteOffsetStartTimestamp =
        onsetTimestamp + this.calculateCorrectNoteOffMin(noteDuration);
      const startAllowance = ONSET_START_ALLOWANCE;
      const endAllowance = 0.25;
      const noteOnTimestamps = new TimingOffsets(
        onsetTimestamp === 0
          ? -0.2
          : onsetTimestamp - startAllowance * this.offsetsConfig.correctNoteOnsetStartPct,
        onsetTimestamp + endAllowance * this.offsetsConfig.correctNoteOnsetEndPct,
        onsetTimestamp === 0
          ? -0.2
          : onsetTimestamp - startAllowance * this.offsetsConfig.mistimedNoteOnsetStartPct,
        onsetTimestamp + endAllowance * this.offsetsConfig.mistimedNoteOnsetEndPct,
      );
      const noteOffTimestamps = new TimingOffsets(
        correctNoteOffsetStartTimestamp,
        offsetTimestamp + endAllowance * this.offsetsConfig.correctNoteOffsetEndPct,
        correctNoteOffsetStartTimestamp - noteDuration * 0.2,
        offsetTimestamp + endAllowance * this.offsetsConfig.mistimedNoteOffsetEndPct,
      );
      if (!note.isRest) {
        events.push({
          note,
          timestamp: onsetTimestamp,
          offsetTime: noteOffTimestamps,
          onsetTime: noteOnTimestamps,
        });
      } else {
        events.push({
          note,
          timestamp: onsetTimestamp,
          offsetTime: noteOffTimestamps,
          onsetTime: noteOnTimestamps,
        });
      }
      t += note.durationRelative;
    }
    this.notesRemaining = events.sort(
      (a, b) => a.onsetTime.mistimedStartTimestamp - b.onsetTime.mistimedStartTimestamp,
    );
    this.totalBeats = t / 0.25;
  }

  pauseAudio() {
    this.audioPaused = true;
    if (this.click) {
      (this.click as Phaser.Sound.WebAudioSound | Phaser.Sound.HTML5AudioSound).setMute(true);
    }
  }

  resumeAudio() {
    this.audioPaused = false;
    (this.click as Phaser.Sound.WebAudioSound | Phaser.Sound.HTML5AudioSound | undefined)?.setMute(
      false,
    );
  }

  /// Moves the cursor back to the starting position and stops movement
  resetHead() {
    this.beatsPlayed = 0;
    this.currentNoteIndex = -1;
    this.isMoving = false;
    this.setX(this.cursorData[0].position.x);
    this.runTime = 0;
    this.draw();
  }

  pause(): void {
    this.wasPausedLastFrame = true;
    this.paused = true;
  }

  resume(): void {
    this.resumedThisFrame = true;
    this.paused = false;
  }

  /* Start moving the cursor. Calls `cb` once the cursor reaches the end. `cb` is bound
   * to the provided `cbScope` object.
   */
  tryStart(cb?: () => void, cbScope?: object): [StartTiming, number] {
    this.onDone = cb;
    this.onDoneCbScope = cbScope;
    const timeDiff = this.lastTimeUpdate + 16.6 - this.lastTick;
    const relTimeDiff = timeDiff / this.msPerTick;
    if (relTimeDiff > 0.2 && relTimeDiff < 0.9) {
      if (relTimeDiff > 0.8) return ['early', timeDiff];
      else return ['late', timeDiff];
    }
    this.lastNoteTime = this.lastTimeUpdate;
    this.resetHead();
    this.isMoving = true;
    this.runTime = 0;
    this.setAlpha(colors.cursorAlpha);
    this.next();
    return ['perfect', timeDiff];
  }

  /**
   * Ungraded: the cursor waits on its first note for `beats` metronome clicks
   * (counting the one waitForInput plays), then starts on the next click on
   * its own, as a key played right on that beat would start it. `onStart`
   * runs then, and `onDone` when it reaches the end (as tryStart's callback).
   * Call before waitForInput.
   */
  startAfterCountIn(beats: number, onStart: () => void, onDone: () => void, scope?: object) {
    this.autoStart = { beats, clicksHeard: 0, onStart, onDone, scope };
    this.pressBeforeStart = null;
  }

  /**
   * Ungraded, Keep practicing: a pass that follows the one before it straight
   * away counts in on that pass's beat grid, so the click that ended it is the
   * count-in's first (rather than a second click a few frames after it). When
   * that click has not sounded yet it is the next one, and counts as the
   * first. Once a beat or more has gone by (the player tapped Keep practicing
   * during the Next ring) the count-in starts afresh, as waitForInput does.
   */
  continueCountIn(beats: number, onStart: () => void, onDone: () => void, scope?: object) {
    const beatTime = this.lastBeatTime;
    const sinceBeat = this.lastFrameTime - beatTime;
    this.startAfterCountIn(beats, onStart, onDone, scope);
    if (!(beatTime > 0) || !this.autoStart || sinceBeat >= this.msPerTick) {
      this.waitForInput();
      return;
    }
    this.autoStart.clicksHeard = sinceBeat < this.msPerTick / 2 ? 1 : 0;
    this.lastTick = beatTime;
    this.started = true;
    this.firstStart = false;
  }

  /**
   * Tap input: from now on passing a note unplayed marks it missed, and the
   * notes already passed unplayed in this run are marked missed now (the pass
   * turned out to be played, so it is graded as a whole).
   */
  startMarkingMissedNotes() {
    this.marksMissedNotes = true;
    this.unmarkedMisses.forEach((missed) =>
      missed.note.playable?.setTimingState(ProgressState.Missed),
    );
    this.unmarkedMisses = [];
  }

  /**
   * Tap input on a touch screen: a key may land `ms` later after each note's
   * onset than usual and still be on time (green), or mistimed (yellow). A
   * touch reaches the app later than a MIDI key does. Early edges stay as they are.
   */
  allowLateOnsetMs(ms: number) {
    // The late edges are 0.25 of a whole note times these shares; a beat is a quarter.
    const share = Math.max(0, ms) / this.msPerTick;
    this.offsetsConfig.correctNoteOnsetEndPct += share;
    this.offsetsConfig.mistimedNoteOnsetEndPct += share;
    this.resetEvents();
  }

  /** The ms until the run starts on its own (startAfterCountIn), when it starts on the next click. */
  msUntilAutoStart(): number | undefined {
    const autoStart = this.autoStart;
    if (!autoStart || this.isMoving || autoStart.clicksHeard < autoStart.beats) return undefined;
    if (!(this.lastTick > 0)) return undefined;
    return Math.max(0, this.lastTick + this.msPerTick - this.lastFrameTime);
  }

  // The ms before a note's onset a key may land: in the yellow zone, and in the green.
  private earlyOnsetWindowMs(onsetPct: number): number {
    return ONSET_START_ALLOWANCE * onsetPct * 4 * this.msPerTick;
  }

  /**
   * Tap input: a key pressed during the count-in, just before the run starts
   * on its own, plays the first note, as it would just after the start: held
   * from the start, timed by how early it came (green or yellow, as a key that
   * early on any other note). Earlier than that, it is not taken (false).
   */
  holdBeforeAutoStart(): boolean {
    const earlyMs = this.msUntilAutoStart();
    if (earlyMs === undefined) return false;
    if (earlyMs > this.earlyOnsetWindowMs(this.offsetsConfig.mistimedNoteOnsetStartPct)) {
      return false;
    }
    this.pressBeforeStart = { earlyMs, released: false };
    return true;
  }

  /** Tap input: the key held before the start came up before the start (false: none held). */
  releaseBeforeAutoStart(): boolean {
    if (!this.pressBeforeStart || this.pressBeforeStart.released) return false;
    this.pressBeforeStart.released = true;
    return true;
  }

  // Tap input: the run just started; play the first note for a key pressed
  // before it.
  private playPressBeforeStart() {
    const press = this.pressBeforeStart;
    this.pressBeforeStart = null;
    const first = this.notesRemaining[0];
    if (!press || !first || first.note.isRest) return;
    const timely =
      press.earlyMs <= this.earlyOnsetWindowMs(this.offsetsConfig.correctNoteOnsetStartPct);
    if (!first.note.playable?.isFocused) first.note.playable?.setFocus(true);
    first.note.playable?.setTimingState(
      timely ? ProgressState.TimelyStart : ProgressState.LateStart,
    );
    this.notesRemaining = this.notesRemaining.filter((x) => x !== first);
    this.notesPlaying.push(first);
    if (press.released) this.onReleaseNote();
  }

  /** Start moving on the beat that started at `beatTime`, with no note played. */
  private startOnBeat(beatTime: number, now: number) {
    const autoStart = this.autoStart;
    if (!autoStart) return;
    this.autoStart = null;
    this.onDone = autoStart.onDone;
    this.onDoneCbScope = autoStart.scope;
    this.lastNoteTime = beatTime;
    this.resetHead();
    this.isMoving = true;
    this.runTime = now - beatTime;
    this.setAlpha(colors.cursorAlpha);
    this.next();
    autoStart.onStart.call(autoStart.scope);
    this.playPressBeforeStart();
  }

  /// Stops the cursor's movement. This does not reset the cursor back to the start.
  stop() {
    this.autoStart = null;
    this.pressBeforeStart = null;
    if (this.lastTick > 0) this.lastBeatTime = this.lastTick;
    this.started = false;
    this.lastTick = 0;
    this.wasPausedLastFrame = false;
    this.resumedThisFrame = false;
  }

  tick(): void {
    console.debug('played', this.beatsPlayed, 'total', this.totalBeats);
    const shouldPlayClick =
      !this.audioPaused && (!this.isMoving || this.beatsPlayed < this.totalBeats);
    if (shouldPlayClick) {
      if (isCapacitorNative()) {
        console.log(
          `[EXERCISE-DBG] CursorLegacy.tick playing click beat=${this.beatsPlayed}/${this.totalBeats} isMoving=${this.isMoving} time=${performance.now().toFixed(0)}`,
        );
        MuseFlowAudio.playSoundEffect({ name: 'click' });
      } else {
        this.click?.play(metronomeSoundConfig());
      }
      if (this.isMoving) {
        this.beatsPlayed++;
      }
    }
    if (this.autoStart && !this.isMoving) this.autoStart.clicksHeard += 1;
    this.setAlpha(colors.cursorAlpha);
  }

  getRelativeTimestamp(): number {
    const durationPerMeasure = (60 / this.tempo) * 4;
    return this.runTime / 1000 / durationPerMeasure;
  }

  checkTiming(_data?: unknown): StartTiming {
    const relTimeDiff = (this.lastTimeUpdate + 16.6 - this.lastTick) / this.msPerTick;
    if (relTimeDiff > 0.15 && relTimeDiff < 0.85) {
      if (relTimeDiff > 0.6) return 'early';
      else return 'late';
    } else return 'perfect';
  }

  onHoldNote(_data?: unknown) {
    if (!this.wasPausedLastFrame && this.notesRemaining.length > 0) {
      console.debug('remaining', this.notesRemaining, this.notesPlaying);
      if (this.notesPlaying.length > 0 && this.notesPlaying[0].note.isRest) {
        this.notesPlaying[0].note.playable?.setTimingState(ProgressState.LateStart);
      }
      //} else {
      const nextNote = this.notesRemaining[0];
      const currentTimestamp = this.getRelativeTimestamp();
      let state: ProgressState | undefined = undefined;
      if (currentTimestamp < nextNote.onsetTime.mistimedStartTimestamp) {
        // the note was played before the yellow zone, do not move on.
        return;
      } else if (currentTimestamp < nextNote.onsetTime.correctStartTimestamp) {
        // the note was played a bit early (in the yellow zone) so we mark
        // it as mistimed
        // LateStart is the same as early start (cause they're functionally the same,
        // atleast for now )
        state = ProgressState.LateStart;
      } else if (currentTimestamp <= nextNote.onsetTime.correctEndTimestamp) {
        // the note was played at the correct onset time (in the green zone), so we mark
        // it as a success
        state = ProgressState.TimelyStart;
      } else if (currentTimestamp <= nextNote.onsetTime.mistimedEndTimestamp) {
        // the note was played too late (in the yellow zone)
        // so we mark it as mistimed
        state = ProgressState.LateStart;
      } else {
        // intentionally empty
      }
      // It is possible that the note is not in focus at the time of holding it (early onset), so we
      // have to focus it manually
      if (!nextNote.note.playable?.isFocused) nextNote.note.playable?.setFocus(true);
      if (state) nextNote.note.playable?.setTimingState(state);
      this.notesRemaining = this.notesRemaining.filter((x) => x !== nextNote);
      this.notesPlaying.push(nextNote);
      //}
    }
  }

  onReleaseNote(_data?: unknown) {
    if (!this.wasPausedLastFrame && this.notesPlaying.length > 0) {
      const currentTimestamp = this.getRelativeTimestamp();
      const currentNote = this.notesPlaying[0];
      const timingOffsets = currentNote.offsetTime;
      if (
        currentTimestamp >= timingOffsets.correctStartTimestamp &&
        currentTimestamp <= timingOffsets.correctEndTimestamp
      ) {
        // Note was released at the right time, mark sucess
        currentNote.note.playable?.setTimingState(ProgressState.Ended);
      } else {
        // Note was not released at the right time, mark mistimed
        currentNote.note.playable?.setTimingState(ProgressState.MistimedRelease);
      }
      this.notesPassed.push(currentNote);
      this.notesPlaying = this.notesPlaying.filter((x) => x !== currentNote);
    }
  }

  lerp = (a: number, b: number, t: number): number => a * (1 - t) + b * t;

  clamp = (a: number, min: number, max: number): number => {
    if (a < min) return min;
    else if (a > max) return max;
    return a;
  };

  next() {
    if (this.currentNoteIndex === this.cursorData.length - 2) {
      const isComplete = this.onDone?.call(this.onDoneCbScope);
      console.log('complete', isComplete);
      if (!isComplete) {
        this.resetHead();
        this.resetEvents();
      } else {
        this.stop();
      }
    } else {
      this.currentNoteIndex++;
    }
  }

  // This function is absolute madness right now
  // I will refactor it later TM
  update(time: number): void {
    this.lastFrameTime = time;
    if (!this.started) return;
    let pauseDuration = 0;
    if (this.wasPausedLastFrame && this.resumedThisFrame) {
      pauseDuration = time - this.lastTimeUpdate;
      this.lastTick += pauseDuration;
      this.lastNoteTime += pauseDuration;
      this.wasPausedLastFrame = false;
      this.resumedThisFrame = false;
    }
    if (this.lastTick === 0) this.lastTick = time;
    const diff = time - this.lastTick;
    if (!this.paused && this.isMoving) {
      this.runTime += time - this.lastTimeUpdate - pauseDuration;
      const currentTimestamp = this.getRelativeTimestamp();
      const nextNote = this.notesRemaining[0];
      if (nextNote && currentTimestamp > nextNote.timestamp && !nextNote.note.playable?.isFocused)
        nextNote.note.playable?.setFocus(true);
      const remainingNotesDiscardList: NoteEvent[] = [];
      if (nextNote) {
        const compTimestamp = nextNote.note.isRest
          ? nextNote.offsetTime.correctStartTimestamp
          : nextNote.onsetTime.mistimedEndTimestamp;
        if (currentTimestamp > compTimestamp) {
          // The user has missed the next note that needed to be played
          // TODO set note state to missed
          //this.notesRemaining.splice(0, 1);
          if (this.marksMissedNotes) nextNote.note.playable?.setTimingState(ProgressState.Missed);
          else this.unmarkedMisses.push(nextNote);
          remainingNotesDiscardList.push(nextNote);
          this.notesPassed.push(nextNote);
        }
      }
      this.notesRemaining = this.notesRemaining.filter(
        (note) => remainingNotesDiscardList.find((x) => note === x) == undefined,
      );
      const playingNotesDiscardList: NoteEvent[] = [];
      for (const note of this.notesPlaying) {
        const currentNote = note;
        if (currentTimestamp > currentNote.offsetTime.correctEndTimestamp) {
          // The user has held the currently playing note for too long,
          // mark it as mistimed
          // earlyrelease is functionally the same as late release
          currentNote.note.playable?.setTimingState(ProgressState.MistimedRelease);
          this.notesPassed.push(currentNote);
          playingNotesDiscardList.push(currentNote);
        }
      }
      this.notesPlaying = this.notesPlaying.filter(
        (note) => playingNotesDiscardList.find((x) => note === x) == undefined,
      );
    }
    if (diff >= this.msPerTick && !this.paused) {
      // If the tick is way behind (e.g., engine restart blocked the
      // frame), skip the click to avoid overlapping sounds. Just
      // reset the timer so the next tick is on schedule.
      if (diff > this.msPerTick * 1.5) {
        console.log(
          `[EXERCISE-DBG] CursorLegacy skipping late tick diff=${diff.toFixed(0)} msPerTick=${this.msPerTick.toFixed(0)}`,
        );
        this.lastTick = time;
      } else {
        this.tick();
        const delay = diff - this.msPerTick;
        this.lastTick = time - delay;
        if (this.autoStart && !this.isMoving && this.autoStart.clicksHeard > this.autoStart.beats) {
          this.startOnBeat(this.lastTick, time);
        }
      }
    }
    const prog = diff / this.msPerTick;
    if (!this.isMoving) this.setAlpha(this.lerp(colors.cursorAlpha, 0, this.clamp(prog, 0, 1)));
    this.lastTimeUpdate = time;
    if (this.isMoving) {
      const timeSinceLastNote = time - this.lastNoteTime;
      const target = this.cursorData[this.currentNoteIndex + 1];
      if (this.currentNoteIndex < 0) {
        throw new Error('current not index is 0');
      }
      const prev = this.cursorData[this.currentNoteIndex];
      const t = timeSinceLastNote / prev.durationInMs;
      const pos = this.lerp(prev.position.x, target.position.x, this.clamp(t, 0, 1));
      this.cursorData[this.currentNoteIndex].playable?.update(pos + this.width / 2);
      this.setX(pos);
      if (t >= 1) {
        this.lastNoteTime = time;
        this.next();
      }
    }
  }

  waitForInput(): void {
    this.lastTick = 0;
    this.tick();
    this.started = true;
    this.firstStart = false;
  }

  resetEvents() {
    this.unmarkedMisses = [];
    this.notesRemaining.clear();
    this.notesPassed.clear();
    this.notesPlaying.clear();
    this.addNoteEvents(this.cursorData);
  }

  /// Resets the cursor. Moves it all the way back to start. And clears all tweens in charge of moving the cursor.
  reset() {
    this.resetEvents();
    this.resetHead();
  }

  destroy(): void {
    this.cursorData.clear();
    this.isMoving = false;
    this.currentNoteIndex = -1;
    super.destroy();
  }

  isActive = (): boolean => this.started;

  // lifted from /Models/EventStream.ts
  private calculateCorrectNoteOffMin(noteDuration: number) {
    if (noteDuration <= 1.0 / 4.0) {
      return noteDuration * 0.5;
    }
    // If duration is between a quarter note and half note, use this block.
    else if (noteDuration > 1.0 / 4.0 && noteDuration < 2.0 / 4.0) {
      const nRange = 2.0 / 4.0 - 1.0 / 4.0;
      // Scale from 0.5 to 0.625 of the note, on a nearly linear curve
      let nScale = 1.0 - (2.0 / 4.0 - noteDuration) / nRange;
      // nScale is normalized from 0..1; putting that on a curve nScale^0.6
      // lets a noteDuration=1.5 have minimum offset of just under 1 beat.
      // Adjust the following line to adjust how "aggressive" scaling between 1 and 2 is.
      // Use graphtoy.com to visualize the curve you're adjusting.
      nScale = Math.pow(nScale, 0.6);
      return 1.0 / 8.0 + 0.1875 * nScale;
    } else {
      return noteDuration - 1.0 / 4.0 + 1.0 / 16.0;
    }
  }
}
